import React, { useEffect } from 'react';

import CustomerReviewsList from 'components/consumer/customer-reviews-list';
import CustomerReviewsHeader from 'components/consumer/customer-reviews-list/CustomerReviewHeader';
import { Helmet } from 'react-helmet-async';
import { logError } from 'commons/logger';
import { usePrevious } from 'react-use';
import useSuspenseQuery from 'commons/useSuspenseQuery';
import { GET_ALL_REVIEWS, GET_REVIEWS_META } from '../../../mocks/queries';
import CustomerReviews from './CustomerReviews';
import withSearchState from './withSearchState';
import { REVIEWS_PAGE_SEO_DESCRIPTION } from '../../../commons/constants';

const CustomerReviewsPage = ({
  config,
  onProduct,
  sortBy,
  searchQuery,
  inSearchMode,
  onSearchUpdate,
  onProductClicked,
  onSortChanged,
  sortSelectOption,
  onProductName,
}) => {
  // To be used later when listing reviews for one product
  const { type, title, subTitle } = config;

  const { data: reviewsMetaData } = useSuspenseQuery(GET_REVIEWS_META, {
    name: 'reviewsMetaData',
  });
  const {
    data,
    error,
    loading,
    refetch,
    fetchMore,
    networkStatus,
  } = useSuspenseQuery(GET_ALL_REVIEWS, {
    variables: {
      sortBy: 'date-desc',
      offset: 0,
      limit: 3,
    },
  });

  const currentReviewsCount = data?.allReviews?.length ?? 0;
  const totalReviewsCount = reviewsMetaData?.reviewsMeta?.count ?? 0;
  const averageScore = reviewsMetaData?.reviewsMeta?.average_score ?? 0;
  const prevOnProduct = usePrevious(onProduct);
  useEffect(() => {
    if (prevOnProduct && !onProduct) {
      // Search cleared
      refetch({
          sortBy,
          offset: 0,
          limit: 3,
      }).then(async () => {
        try {
          // Temp Fix for refetch not using new variables
          await fetchMore({
            variables: {
              sortBy,
              offset: 0,
              limit: 3,
            },
            updateQuery: (prevResult, { fetchMoreResult }) => ({
              allReviews: [...fetchMoreResult.allReviews],
            }),
          });
        } catch (err) {
          logError(err);
        }
      });
    }
  }, [fetchMore, onProduct, prevOnProduct, refetch, sortBy]);

  return (
    <>
      <Helmet>
        <meta name="description" content={REVIEWS_PAGE_SEO_DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Reviews by Joybird.com" />

        <meta
          property="og:description"
          content={REVIEWS_PAGE_SEO_DESCRIPTION}
        />

        <meta
          property="og:image"
          content="https://joybird2.imgix.net/meta/jb0916-FacebookPageThumbnails-Reviews-v1.jpg?auto=compress,format"
        />

        <meta property="og:url" content="https://joybird.com/reviews/" />
      </Helmet>

      <CustomerReviewsHeader
        title={title}
        subTitle={subTitle}
        type={type}
        averageScore={averageScore}
        totalReviewsCount={totalReviewsCount}
        refetch={refetch}
        searchState={{
          inSearchMode,
          onSearchUpdate,
          onProductClicked,
          searchQuery,
          onProduct,
        }}
        onSortChanged={onSortChanged}
        sortSelectOption={sortSelectOption}
      />
      {!onProduct ? (
        <CustomerReviewsList
          config={{
            type,
            reviews: data?.allReviews,
            refetch,
            networkStatus,
            loading,
            error,
            loadMore: async () => {
              try {
                await fetchMore({
                  variables: {
                    offset: currentReviewsCount,
                    sortBy,
                  },
                  updateQuery: (prevResults, { fetchMoreResult }) => {
                    if (!fetchMoreResult) return prevResults;
                    return {
                      allReviews: [
                        ...prevResults.allReviews,
                        ...fetchMoreResult.allReviews,
                      ],
                    };
                  },
                });
              } catch (err) {
                logError(err);
              }
            },
            totalReviewsCount,
            averageScore,
          }}
        />
      ) : (
        <CustomerReviews
          id={onProduct}
          productName={onProductName}
          forComponent="searchAllReviews"
          sortBy={sortBy}
          sortSelectOption={sortSelectOption}
        />
      )}
    </>
  );
};

export default withSearchState(CustomerReviewsPage);
